// Click-to-call, Phase 1 (ring my phone, then connect). The browser side of the twilio-outbound-call Edge Function.
// Target: { sessionId } from a Communications conversation, or { accountId, clientPhone } from a CRM card.

const E164 = /^\+[1-9]\d{6,14}$/;

export function canCallConversation(session) {
    const phone = typeof session?.contact_phone === 'string' ? session.contact_phone.trim() : '';
    return Boolean(session?.id) && E164.test(phone);
}

/**
 * A fresh one-time key per confirm. A double click, or a retry after a network error or timeout, reuses it, so the server
 * can only ever ring once. After the server definitely refused (it answered with its own error), the button mints a new key.
 */
export function newCallKey() {
    if (typeof globalThis.crypto?.randomUUID === 'function') return globalThis.crypto.randomUUID();
    const hex = Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-4${hex.slice(13, 16)}-a${hex.slice(17, 20)}-${hex.slice(20, 32)}`;
}

function targetBody(target) {
    if (target?.sessionId) return { sessionId: target.sessionId };
    return { accountId: target?.accountId, clientPhone: target?.clientPhone };
}

async function invoke(client, body) {
    const { data, error } = await client.functions.invoke('twilio-outbound-call', { body });
    if (error) {
        let details = null;
        try { details = await error.context?.json?.(); } catch { /* body may already be read */ }
        const failure = new Error(details?.error || error.message || 'The call could not be started.');
        failure.code = details?.code || null;
        failure.status = typeof error.context?.status === 'number' ? error.context.status : null;
        // Definite: our function itself answered with { ok: false }. A gateway timeout or an unreadable body is not definite
        // (the request may still be running), and neither is a network failure (no response at all).
        failure.definite = Boolean(details && details.ok === false);
        throw failure;
    }
    if (!data?.ok) {
        const failure = new Error(data?.error || 'The call could not be started.');
        failure.code = data?.code || null;
        failure.status = 200;
        failure.definite = Boolean(data && data.ok === false);
        throw failure;
    }
    return data;
}

export function prepareOutboundCall(client, target) {
    return invoke(client, { action: 'prepare', ...targetBody(target) });
}

export function placeOutboundCall(client, target, { numberId, idempotencyKey }) {
    return invoke(client, { action: 'call', ...targetBody(target), numberId: numberId || undefined, idempotencyKey });
}

/** One short phrase for an outbound call line in a conversation. */
export function outboundCallStatusLabel(status) {
    switch (status) {
        case 'answered': return 'connected';
        case 'completed': return 'answered';
        case 'no_answer': return 'no answer';
        case 'busy': return 'busy';
        case 'failed': return 'failed';
        case 'canceled': return 'not connected';
        case 'staff_no_answer': return 'not placed (your phone was not answered)';
        case 'client_ringing': return 'ringing';
        default: return 'calling';
    }
}

/** "Outbound call · by Val Whitney · 1m 13s · answered" */
export function outboundCallSummary({ staffName, duration, status }) {
    const parts = ['Outbound call'];
    if (staffName) parts.push(`by ${staffName}`);
    if (Number.isFinite(duration) && duration > 0) {
        parts.push(duration >= 60 ? `${Math.floor(duration / 60)}m ${duration % 60}s` : `${duration}s`);
    }
    parts.push(outboundCallStatusLabel(status));
    return parts.join(' · ');
}
